import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { api, formatApiErrorDetail, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY_FORM = {
  name: "",
  amazon_url: "",
  image_url: "",
  description: "",
  cta_text: "Open in Amazon App",
  rating: 0,
  review_count: 0,
  active: true,
  popup_enabled: true,
  description_enabled: true,
};

export default function Admin() {
  const { user, logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("products"); // products | analytics | leads
  const [analyticsDays, setAnalyticsDays] = useState(7);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [editing, setEditing] = useState(null); // product being edited or "new"
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState(null);

  const load = async () => {
    try {
      const [{ data: p }, { data: l }] = await Promise.all([
        api.get("/admin/products"),
        api.get("/admin/leads"),
      ]);
      setProducts(p);
      setLeads(l);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const loadAnalytics = async (days) => {
    setAnalyticsLoading(true);
    try {
      const { data } = await api.get(`/admin/analytics?days=${days}`);
      setAnalytics(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load analytics");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "analytics") loadAnalytics(analyticsDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, analyticsDays]);

  const openNew = () => {
    setEditing("new");
    setForm(EMPTY_FORM);
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name || "",
      amazon_url: p.amazon_url || "",
      image_url: p.image_url || "",
      description: p.description || "",
      cta_text: p.cta_text || "Open in Amazon App",
      rating: typeof p.rating === "number" ? p.rating : 0,
      review_count: typeof p.review_count === "number" ? p.review_count : 0,
      active: p.active !== false,
      popup_enabled: p.popup_enabled !== false,
      description_enabled: p.description_enabled !== false,
    });
  };

  const closeForm = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onSave = async () => {
    if (!form.name || !form.amazon_url) {
      toast.error("Name and Amazon URL are required");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        const { data } = await api.post("/admin/products", form);
        setProducts((prev) => [...prev, data]);
        toast.success("Product added");
      } else {
        const { data } = await api.put(`/admin/products/${editing}`, form);
        setProducts((prev) => prev.map((p) => (p.id === editing ? data : p)));
        toast.success("Product updated");
      }
      closeForm();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      setProducts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Product deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const onToggle = async (id) => {
    try {
      const { data } = await api.patch(`/admin/products/${id}/toggle`);
      setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, active: data.active } : p)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Toggle failed");
    }
  };

  const persistOrder = async (ordered) => {
    setProducts(ordered);
    try {
      await api.post("/admin/products/reorder", { ids: ordered.map((p) => p.id) });
    } catch (e) {
      toast.error("Reorder save failed");
    }
  };

  const onDragStart = (id) => setDragId(id);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const idxFrom = products.findIndex((p) => p.id === dragId);
    const idxTo = products.findIndex((p) => p.id === targetId);
    if (idxFrom === -1 || idxTo === -1) return;
    const arr = [...products];
    const [moved] = arr.splice(idxFrom, 1);
    arr.splice(idxTo, 0, moved);
    setDragId(null);
    persistOrder(arr);
  };

  return (
    <div className="min-h-screen bg-[#F4F1EA] text-[#1E1E24]" data-testid="admin-page">
      <Toaster position="top-right" richColors />
      {/* Top bar */}
      <header className="bg-[#FFFDF9] border-b-2 border-[#1E1E24]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="admin-brand">
            <span className="text-2xl">🦡</span>
            <span className="font-headings text-xl font-bold">
              Jungle <span className="text-[#FF5A5F]">Badger</span> Admin
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#5E5E66] hidden sm:inline" data-testid="admin-user-email">
              {user?.email}
            </span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="bg-white text-[#1E1E24] border-2 border-[#1E1E24] rounded-full hover:bg-[#FFB400] transition-all font-bold px-5 py-2 text-sm"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        {/* Tabs */}
        <div className="flex items-center gap-2 mb-8">
          <button
            data-testid="tab-products"
            onClick={() => setTab("products")}
            className={`px-5 py-2 rounded-full border-2 border-[#1E1E24] font-bold text-sm transition-all ${
              tab === "products"
                ? "bg-[#1E1E24] text-white"
                : "bg-white text-[#1E1E24] hover:bg-[#F4F1EA]"
            }`}
          >
            Products ({products.length})
          </button>
          <button
            data-testid="tab-analytics"
            onClick={() => setTab("analytics")}
            className={`px-5 py-2 rounded-full border-2 border-[#1E1E24] font-bold text-sm transition-all ${
              tab === "analytics"
                ? "bg-[#1E1E24] text-white"
                : "bg-white text-[#1E1E24] hover:bg-[#F4F1EA]"
            }`}
          >
            Analytics
          </button>
          <button
            data-testid="tab-leads"
            onClick={() => setTab("leads")}
            className={`px-5 py-2 rounded-full border-2 border-[#1E1E24] font-bold text-sm transition-all ${
              tab === "leads"
                ? "bg-[#1E1E24] text-white"
                : "bg-white text-[#1E1E24] hover:bg-[#F4F1EA]"
            }`}
          >
            Leads ({leads.length})
          </button>
        </div>

        {tab === "products" && (
          <section>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div>
                <h1 className="font-headings text-3xl md:text-4xl font-bold">Products</h1>
                <p className="text-[#5E5E66] text-sm">Drag rows to reorder. Toggle to show/hide.</p>
              </div>
              <button
                data-testid="add-product-button"
                onClick={openNew}
                className="bg-[#FF5A5F] text-white border-2 border-[#1E1E24] rounded-full shadow-[4px_4px_0px_0px_#1E1E24] hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_#1E1E24] transition-all font-bold px-6 py-2.5"
              >
                + Add product
              </button>
            </div>

            <div className="bg-white border-2 border-[#1E1E24] rounded-2xl overflow-hidden shadow-[6px_6px_0px_0px_#1E1E24]">
              {loading ? (
                <div className="p-10 text-center text-[#5E5E66]">Loading...</div>
              ) : products.length === 0 ? (
                <div className="p-10 text-center" data-testid="admin-products-empty">
                  <div className="text-4xl mb-3">🦡</div>
                  <p className="text-[#5E5E66]">No products yet. Click <b>Add product</b> to create your first one.</p>
                </div>
              ) : (
                <table className="w-full text-left">
                  <thead className="bg-[#F4F1EA] border-b-2 border-[#1E1E24]">
                    <tr className="font-headings text-sm">
                      <th className="p-4 w-10"></th>
                      <th className="p-4">Product</th>
                      <th className="p-4 hidden md:table-cell">Rating</th>
                      <th className="p-4 hidden lg:table-cell">Amazon</th>
                      <th className="p-4">Active</th>
                      <th className="p-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p) => (
                      <tr
                        key={p.id}
                        draggable
                        onDragStart={() => onDragStart(p.id)}
                        onDragOver={onDragOver}
                        onDrop={() => onDrop(p.id)}
                        data-testid={`admin-product-row-${p.id}`}
                        className={`border-b border-[#1E1E24]/10 hover:bg-[#F4F1EA]/50 transition-colors ${
                          dragId === p.id ? "opacity-50" : ""
                        }`}
                      >
                        <td className="p-4 cursor-grab active:cursor-grabbing text-[#5E5E66] text-xl select-none">⋮⋮</td>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg border-2 border-[#1E1E24] bg-[#F4F1EA] overflow-hidden flex items-center justify-center flex-shrink-0">
                              {p.image_url ? (
                                <img src={resolveImageUrl(p.image_url)} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <span>🦡</span>
                              )}
                            </div>
                            <div>
                              <div className="font-bold">{p.name}</div>
                              <div className="text-xs text-[#5E5E66] line-clamp-1 max-w-xs">{p.description}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 hidden md:table-cell">
                          {p.rating > 0 ? (
                            <span className="text-sm font-medium">
                              ★ {Number(p.rating).toFixed(1)}
                              {p.review_count > 0 && (
                                <span className="text-[#5E5E66] ml-1">
                                  ({Number(p.review_count).toLocaleString()})
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-[#5E5E66] text-sm">—</span>
                          )}
                        </td>
                        <td className="p-4 hidden lg:table-cell">
                          <a
                            href={p.amazon_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#3A86FF] hover:underline text-sm font-medium truncate max-w-[200px] inline-block align-middle"
                          >
                            {p.amazon_url}
                          </a>
                        </td>
                        <td className="p-4">
                          <button
                            data-testid={`toggle-active-${p.id}`}
                            onClick={() => onToggle(p.id)}
                            className={`w-12 h-7 rounded-full border-2 border-[#1E1E24] relative transition-colors ${
                              p.active ? "bg-[#00A676]" : "bg-white"
                            }`}
                            aria-label={p.active ? "Active" : "Inactive"}
                          >
                            <span
                              className={`absolute top-0.5 w-5 h-5 rounded-full border-2 border-[#1E1E24] bg-white transition-all ${
                                p.active ? "left-5" : "left-0.5"
                              }`}
                            />
                          </button>
                        </td>
                        <td className="p-4 text-right whitespace-nowrap">
                          <button
                            data-testid={`edit-product-${p.id}`}
                            onClick={() => openEdit(p)}
                            className="text-sm font-bold text-[#1E1E24] hover:text-[#FF5A5F] mr-3"
                          >
                            Edit
                          </button>
                          <button
                            data-testid={`delete-product-${p.id}`}
                            onClick={() => onDelete(p.id)}
                            className="text-sm font-bold text-[#5E5E66] hover:text-[#FF5A5F]"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        )}

        {tab === "analytics" && (
          <section data-testid="analytics-section">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
              <div>
                <h1 className="font-headings text-3xl md:text-4xl font-bold">Analytics</h1>
                <p className="text-[#5E5E66] text-sm">
                  First-party tracking. Independent of Meta / Google — ad-blocker proof.
                </p>
              </div>
              <div className="flex items-center gap-2" data-testid="analytics-range">
                {[1, 7, 30, 90].map((d) => (
                  <button
                    key={d}
                    data-testid={`analytics-range-${d}`}
                    onClick={() => setAnalyticsDays(d)}
                    className={`px-4 py-2 rounded-full border-2 border-[#1E1E24] font-bold text-xs transition-all ${
                      analyticsDays === d
                        ? "bg-[#1E1E24] text-white"
                        : "bg-white text-[#1E1E24] hover:bg-[#F4F1EA]"
                    }`}
                  >
                    {d === 1 ? "Today" : `${d}d`}
                  </button>
                ))}
              </div>
            </div>

            {analyticsLoading || !analytics ? (
              <div className="p-10 text-center text-[#5E5E66]">Loading analytics...</div>
            ) : (
              <>
                {/* Totals */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="analytics-totals">
                  {[
                    { label: "Total views", value: analytics.totals.views, testid: "stat-views" },
                    { label: "Amazon clicks", value: analytics.totals.clicks, testid: "stat-clicks" },
                    { label: "CTR", value: `${analytics.totals.ctr}%`, testid: "stat-ctr" },
                    { label: "Leads", value: analytics.totals.leads, testid: "stat-leads" },
                  ].map((s) => (
                    <div
                      key={s.label}
                      data-testid={s.testid}
                      className="bg-white border-2 border-[#1E1E24] rounded-2xl p-5 shadow-[4px_4px_0px_0px_#1E1E24]"
                    >
                      <p className="text-xs font-bold text-[#5E5E66] uppercase tracking-wide">{s.label}</p>
                      <p className="font-headings text-3xl font-bold mt-1">{s.value}</p>
                    </div>
                  ))}
                </div>

                {/* Popup impact */}
                <div className="bg-[#FFB400]/20 border-2 border-[#1E1E24] rounded-2xl p-5 mb-8" data-testid="popup-impact">
                  <p className="font-headings font-bold text-lg mb-1">Popup impact</p>
                  <p className="text-sm text-[#5E5E66]">
                    Shown <b className="text-[#1E1E24]">{analytics.totals.popup_shown}</b> times ·
                    dismissed <b className="text-[#1E1E24]">{analytics.totals.popup_dismissed}</b> times ·
                    dismiss rate <b className="text-[#1E1E24]">{analytics.totals.popup_dismiss_rate}%</b>
                  </p>
                </div>

                {/* Per-product table */}
                <div className="bg-white border-2 border-[#1E1E24] rounded-2xl overflow-hidden shadow-[6px_6px_0px_0px_#1E1E24]">
                  {analytics.per_product.length === 0 ? (
                    <div className="p-10 text-center text-[#5E5E66]">No product data yet.</div>
                  ) : (
                    <table className="w-full text-left" data-testid="analytics-per-product">
                      <thead className="bg-[#F4F1EA] border-b-2 border-[#1E1E24]">
                        <tr className="font-headings text-sm">
                          <th className="p-4">Product</th>
                          <th className="p-4 text-right">Views</th>
                          <th className="p-4 text-right">Clicks</th>
                          <th className="p-4 text-right">CTR</th>
                          <th className="p-4 text-right hidden md:table-cell">Leads</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.per_product.map((row) => (
                          <tr
                            key={row.slug || row.name}
                            data-testid={`analytics-row-${row.slug || "unknown"}`}
                            className="border-b border-[#1E1E24]/10 last:border-b-0"
                          >
                            <td className="p-4 font-medium">{row.name}</td>
                            <td className="p-4 text-right tabular-nums">{row.views}</td>
                            <td className="p-4 text-right tabular-nums">{row.clicks}</td>
                            <td className="p-4 text-right tabular-nums font-bold">
                              {row.views ? `${row.ctr}%` : "—"}
                            </td>
                            <td className="p-4 text-right tabular-nums hidden md:table-cell">{row.leads}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <p className="text-xs text-[#5E5E66] mt-4">
                  Views count both product pages and the home page ({analytics.totals.home_views} home views in this range).
                  Leads count email popup + inline form submissions tagged to each product.
                </p>
              </>
            )}
          </section>
        )}

        {tab === "leads" && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-headings text-3xl md:text-4xl font-bold">Leads</h1>
                <p className="text-[#5E5E66] text-sm">
                  All emails captured from the site. Also pushed to Google Sheets when configured.
                </p>
              </div>
            </div>
            <div className="bg-white border-2 border-[#1E1E24] rounded-2xl overflow-hidden shadow-[6px_6px_0px_0px_#1E1E24]">
              {leads.length === 0 ? (
                <div className="p-10 text-center" data-testid="admin-leads-empty">
                  <div className="text-4xl mb-3">✉️</div>
                  <p className="text-[#5E5E66]">No leads yet.</p>
                </div>
              ) : (
                <table className="w-full text-left">
                  <thead className="bg-[#F4F1EA] border-b-2 border-[#1E1E24]">
                    <tr className="font-headings text-sm">
                      <th className="p-4">When</th>
                      <th className="p-4">Email</th>
                      <th className="p-4 hidden md:table-cell">Page</th>
                      <th className="p-4 hidden lg:table-cell">UTM</th>
                      <th className="p-4">Sheets</th>
                      <th className="p-4">Beehiiv</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l) => (
                      <tr key={l.id} className="border-b border-[#1E1E24]/10">
                        <td className="p-4 text-sm text-[#5E5E66] whitespace-nowrap">
                          {new Date(l.created_at).toLocaleString()}
                        </td>
                        <td className="p-4 font-medium">{l.email}</td>
                        <td className="p-4 hidden md:table-cell text-sm text-[#5E5E66]">{l.page || "—"}</td>
                        <td className="p-4 hidden lg:table-cell text-sm text-[#5E5E66]">
                          {l.utm_source ? `${l.utm_source} / ${l.utm_campaign || "—"}` : "—"}
                        </td>
                        <td className="p-4">
                          {l.synced_to_sheet ? (
                            <span className="text-[#00A676] font-bold text-sm">✓</span>
                          ) : (
                            <span className="text-[#5E5E66] text-sm">—</span>
                          )}
                        </td>
                        <td className="p-4">
                          {l.synced_to_beehiiv ? (
                            <span className="text-[#00A676] font-bold text-sm">✓</span>
                          ) : (
                            <span className="text-[#5E5E66] text-sm">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        )}
      </main>

      {/* Product form modal */}
      {editing !== null && (
        <div
          className="fixed inset-0 bg-[#1E1E24]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={closeForm}
          data-testid="admin-form-overlay"
        >
          <div
            className="bg-[#FFFDF9] border-4 border-[#1E1E24] rounded-3xl p-8 max-w-2xl w-full my-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeForm}
              data-testid="admin-form-close"
              className="absolute -top-4 -right-4 w-10 h-10 bg-white border-2 border-[#1E1E24] rounded-full flex items-center justify-center shadow-[4px_4px_0px_0px_#1E1E24] hover:bg-[#FF5A5F] hover:text-white transition-colors font-bold text-lg"
              aria-label="Close"
            >
              ×
            </button>
            <h2 className="font-headings text-2xl md:text-3xl font-bold mb-6">
              {editing === "new" ? "Add product" : "Edit product"}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-bold uppercase tracking-wider mb-1.5">
                  Product name *
                </label>
                <input
                  data-testid="form-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-full border-2 border-[#1E1E24] bg-white focus:outline-none focus:ring-4 focus:ring-[#FFB400]"
                  placeholder="HOVERAir X1 Drone"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-bold uppercase tracking-wider mb-1.5">
                  Amazon URL *
                </label>
                <input
                  data-testid="form-amazon-url"
                  type="url"
                  value={form.amazon_url}
                  onChange={(e) => setForm({ ...form, amazon_url: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-full border-2 border-[#1E1E24] bg-white focus:outline-none focus:ring-4 focus:ring-[#FFB400]"
                  placeholder="https://amazon.com/..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-bold uppercase tracking-wider mb-1.5">
                  Short description
                </label>
                <textarea
                  data-testid="form-description"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-2xl border-2 border-[#1E1E24] bg-white focus:outline-none focus:ring-4 focus:ring-[#FFB400] resize-none"
                  placeholder="The easiest drone you will ever use!"
                />
                <p className="text-xs text-[#5E5E66] mt-1">Shown under the product title.</p>
              </div>

              <div>
                <label className="block text-sm font-bold uppercase tracking-wider mb-1.5">Rating (0-5)</label>
                <input
                  data-testid="form-rating"
                  type="number"
                  step="0.1"
                  min="0"
                  max="5"
                  value={form.rating}
                  onChange={(e) => setForm({ ...form, rating: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2.5 rounded-full border-2 border-[#1E1E24] bg-white focus:outline-none focus:ring-4 focus:ring-[#FFB400]"
                  placeholder="4.7"
                />
              </div>

              <div>
                <label className="block text-sm font-bold uppercase tracking-wider mb-1.5">Review count</label>
                <input
                  data-testid="form-review-count"
                  type="number"
                  min="0"
                  value={form.review_count}
                  onChange={(e) => setForm({ ...form, review_count: parseInt(e.target.value, 10) || 0 })}
                  className="w-full px-4 py-2.5 rounded-full border-2 border-[#1E1E24] bg-white focus:outline-none focus:ring-4 focus:ring-[#FFB400]"
                  placeholder="2341"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-bold uppercase tracking-wider mb-1.5">CTA button text</label>
                <input
                  data-testid="form-cta"
                  type="text"
                  value={form.cta_text}
                  onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-full border-2 border-[#1E1E24] bg-white focus:outline-none focus:ring-4 focus:ring-[#FFB400]"
                  placeholder="Open in Amazon App"
                />
                <p className="text-xs text-[#5E5E66] mt-1">Label for the coral button that opens Amazon.</p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-bold uppercase tracking-wider mb-1.5">
                  Product image
                </label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-2xl border-2 border-[#1E1E24] bg-[#F4F1EA] overflow-hidden flex items-center justify-center flex-shrink-0">
                    {form.image_url ? (
                      <img src={resolveImageUrl(form.image_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-2xl">🦡</span>
                    )}
                  </div>
                  <label className="cursor-pointer bg-white text-[#1E1E24] border-2 border-[#1E1E24] rounded-full hover:bg-[#FFB400] transition-all font-bold px-5 py-2 text-sm">
                    {uploading ? "Uploading..." : form.image_url ? "Change image" : "Upload image"}
                    <input
                      data-testid="form-image-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onUpload(e.target.files?.[0])}
                    />
                  </label>
                  {form.image_url && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, image_url: "" })}
                      className="text-sm text-[#5E5E66] hover:text-[#FF5A5F] font-bold"
                      data-testid="form-image-clear"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>

              <div className="md:col-span-2 flex items-center gap-3">
                <input
                  data-testid="form-active"
                  type="checkbox"
                  id="active"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="w-5 h-5 rounded border-2 border-[#1E1E24]"
                />
                <label htmlFor="active" className="font-bold text-sm">
                  Active (visible on landing page)
                </label>
              </div>

              <div className="md:col-span-2 flex items-center gap-3">
                <input
                  data-testid="form-popup-enabled"
                  type="checkbox"
                  id="popup_enabled"
                  checked={form.popup_enabled}
                  onChange={(e) => setForm({ ...form, popup_enabled: e.target.checked })}
                  className="w-5 h-5 rounded border-2 border-[#1E1E24]"
                />
                <label htmlFor="popup_enabled" className="font-bold text-sm">
                  Show email popup on this product page
                </label>
              </div>

              <div className="md:col-span-2 flex items-center gap-3">
                <input
                  data-testid="form-description-enabled"
                  type="checkbox"
                  id="description_enabled"
                  checked={form.description_enabled}
                  onChange={(e) => setForm({ ...form, description_enabled: e.target.checked })}
                  className="w-5 h-5 rounded border-2 border-[#1E1E24]"
                />
                <label htmlFor="description_enabled" className="font-bold text-sm">
                  Show short description on this product page
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t-2 border-[#1E1E24]/10">
              <button
                onClick={closeForm}
                data-testid="form-cancel"
                className="bg-white text-[#1E1E24] border-2 border-[#1E1E24] rounded-full hover:bg-[#F4F1EA] transition-all font-bold px-6 py-2.5"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={saving}
                data-testid="form-save"
                className="bg-[#FF5A5F] text-white border-2 border-[#1E1E24] rounded-full shadow-[4px_4px_0px_0px_#1E1E24] hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_#1E1E24] transition-all font-bold px-6 py-2.5 disabled:opacity-60"
              >
                {saving ? "Saving..." : editing === "new" ? "Create product" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
